import * as React from 'react';
import ReactDatePicker from 'react-datepicker';
import { Bem, addClass } from '../../utils/css.js';
import { warnOnce, callVoids, wrapEventHandlers } from '../../utils/common.js';
import './DatePickerBase.scss.js';
import './DatePicker.scss.js';
import { Placement, Key } from '../../constants.js';
import { InputField } from '../InputField/InputField.js';
import { useId } from '../../utils/hooks/useId.js';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { Icon } from '../Icon/Icon.js';

const bem = Bem.create("vai-datepicker");
const defaultProps = {
  placeholder: "Select...",
  dateFormat: "P",
  config: {
    yearDropdownItemNumber: 3,
    nextMonthButtonLabel: "",
    previousMonthButtonLabel: "",
    popperPlacement: Placement.TopEnd
  }
};
const CustomInput = ({ width, disabled, invalid, datepickerRef, toggleDatePicker, ourRef, ...rest }) => {
  React.useEffect(() => {
    datepickerRef(ourRef.current);
  }, [datepickerRef, ourRef]);
  return /* @__PURE__ */ React.createElement("div", {
    className: bem.element("input")(disabled && "disabled")
  }, /* @__PURE__ */ React.createElement(InputField, {
    dataTa: "date-input",
    ...rest,
    innerRef: ourRef,
    width,
    disabled,
    invalid
  }), /* @__PURE__ */ React.createElement(Icon, {
    name: VaiIcon.Calendar,
    onClick: () => !disabled && toggleDatePicker()
  }));
};
const DatePicker = (props) => {
  const {
    dataTa,
    htmlId,
    className,
    invalid,
    disabled,
    width,
    placeholder,
    config,
    value,
    name,
    dateFormat,
    todayButton,
    startDate,
    endDate,
    minDate,
    maxDate,
    onChange,
    onSelect,
    onCalendarClose,
    onCalendarOpen,
    onKeyDown,
    calendarClassName,
    ...rest
  } = props;
  const newProperties = {
    name,
    dateFormat,
    todayButton,
    startDate,
    endDate,
    minDate,
    maxDate,
    onChange,
    onSelect,
    onCalendarClose,
    onCalendarOpen
  };
  if (Object.keys(rest).length > 0) {
    warnOnce(`DatePicker: The following props are deprecated and will be moved to the config prop:

${Object.keys(rest).join("\n")}.`);
  }
  if (onKeyDown !== void 0 && config?.onKeyDown !== void 0) {
    warnOnce(`DatePicker: Both onKeyDown and config.onKeyDown are defined. The config.onKeyDown will be used.
Remove the onKeyDown prop to remove this warning. It is deprecated and will be removed in a future version.`);
  }
  if (calendarClassName !== void 0 && config?.calendarClassName !== void 0) {
    warnOnce(`DatePicker: Both calendarClassName and config.calendarClassName are defined. The config.calendarClassName will be used.
Remove the calendarClassName prop to remove this warning. It is deprecated and will be removed in a future version.`);
  }
  if (rest.disabledKeyboardNavigation || config?.disabledKeyboardNavigation) {
    warnOnce(`DatePicker: You should not use the disabledKeyboardNavigation prop.
It is bad for accessibility.`);
  }
  const datePickerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const inputFocusHandler = () => {
    setTimeout(() => inputRef.current?.focus());
  };
  const handleKeydown = (e) => {
    if (e.key === Key.Enter) {
      if (!datePickerRef.current?.isCalendarOpen()) {
        datePickerRef.current?.setOpen(true);
        inputRef.current?.blur();
      }
    }
  };
  const id = useId(htmlId, ["datepicker"]);
  const toggleDatepickerIsOpen = () => {
    if (!datePickerRef.current?.isCalendarOpen()) {
      datePickerRef.current?.setOpen(true);
    }
  };
  return /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    "data-ta": dataTa,
    className: addClass(bem.block(), className)
  }, /* @__PURE__ */ React.createElement(ReactDatePicker, {
    disabled,
    ref: datePickerRef,
    openToDate: value,
    selected: value,
    placeholderText: placeholder,
    autoComplete: "off",
    customInputRef: "datepickerRef",
    calendarClassName: addClass(bem.element("calendar")(), config?.calendarClassName ?? calendarClassName),
    customInput: /* @__PURE__ */ React.createElement(CustomInput, {
      ourRef: inputRef,
      toggleDatePicker: toggleDatepickerIsOpen,
      width,
      disabled,
      invalid
    }),
    fixedHeight: true,
    showMonthDropdown: true,
    showYearDropdown: true,
    preventOpenOnFocus: true,
    ...rest,
    ...newProperties,
    ...defaultProps.config,
    ...config,
    onCalendarClose: callVoids(inputFocusHandler, onCalendarClose),
    onKeyDown: wrapEventHandlers(handleKeydown, config?.onKeyDown ?? onKeyDown ?? void 0)
  }));
};
DatePicker.metaProperties = ["invalid", "disabled"];
DatePicker.defaultProps = defaultProps;

export { DatePicker };
